import { useTranslation } from 'react-i18next';
import { Card, toNumberString, useRouteDocumentTitle } from '@client/toolkit';
import { ReportingPaths } from '../reporting.router';
import { ConsumptionBySourceTable } from './components/consumption-source-table';
import { ConsumptionBySourceGraphSection, useConsumptionBySourceQuery } from './components/consumption-source-graph';
import { ReportPageContent } from '../../../components/layout/report-page-content';
import { DashboardFiltersProvider } from '../../dashboard/context/dashboard-filters.context';
import { DashboardFilters } from '../../dashboard/components';
import { EosSourcesFilter, EosSourcesFilterContextProvider } from '../../../components/eos/filters/eos-sources-filter';
import { eosDateRangeFilterVisiblePresets } from '../../../components/eos/config';
import { StatCard } from '../../../components/eos';
import { getSourceDisplayName } from '../reporting-consumption-energy-type/components/consumption-source-tag';

function EosSourceTotalConsumptionStatCard() {
  const { t } = useTranslation('eos');
  const { sources, sourceTotals } = useConsumptionBySourceQuery();
  if (sources.length === 0) {
    return <StatCard title={t('totalConsumption')} value="-" />;
  }
  return (
    <>
      {sources.map((source) => {
        const total = sourceTotals[source] ?? 0;
        return (
          <StatCard
            key={source}
            title={t('totalConsumptionSource', { source: getSourceDisplayName(source) })}
            value={total > 0 ? `${toNumberString(total)} kWh` : '-'}
          />
        );
      })}
    </>
  );
}

function EosSourceTotalProducedStatCard() {
  const { t } = useTranslation('eos');
  const { sources, exportTotals } = useConsumptionBySourceQuery();
  const sourcesWithExport = sources.filter((s) => (exportTotals[s] ?? 0) > 0);
  if (sourcesWithExport.length === 0) return null;
  return (
    <>
      {sourcesWithExport.map((source) => (
        <StatCard
          key={source}
          title={`${t('totalProduced')} (${getSourceDisplayName(source)})`}
          value={`${toNumberString(exportTotals[source])} kWh`}
        />
      ))}
    </>
  );
}

function EosSourceStatsCards() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <EosSourceTotalConsumptionStatCard />
      <EosSourceTotalProducedStatCard />
    </div>
  );
}

export const ReportingConsumptionBySourceRoute = () => {
  const { t } = useTranslation('reporting');
  useRouteDocumentTitle(t('reporting.documentTitle'), ReportingPaths.ConsumptionBySource);
  return (
    <DashboardFiltersProvider dateParamsPrefix="eos">
      <EosSourcesFilterContextProvider>
        <ReportPageContent
          breadcrumb={t('consumptionBySourceTitle')}
          menu={<DashboardFilters showOnlySpacesWithEosEnabled visiblePresets={eosDateRangeFilterVisiblePresets} />}
        >
          <EosSourcesFilter />
          <EosSourceStatsCards />
          <ConsumptionBySourceGraphSection />
          <Card>
            <ConsumptionBySourceTable />
          </Card>
        </ReportPageContent>
      </EosSourcesFilterContextProvider>
    </DashboardFiltersProvider>
  );
};
